import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData, UseMutationResult } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VersionNavigation } from "@/components/VersionNavigation";
import { useState, useEffect, useMemo } from "react";
import { CheckIcon } from "@radix-ui/react-icons"
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { User, useUser } from "../../context/UserContext";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

// Helper function to get current user ID from meta tag
const getCurrentUserId = () => {
    const userIdTag = document.querySelector("meta[name='current-user-id']");
    if (!userIdTag) {
        console.warn("Jelenlegi felhasználói azonosító meta tag nem található!");
        return null;
    }
    return userIdTag.getAttribute("content");
};

// Helper function for pagination
const generatePaginationPages = (currentPage: number, totalPages: number, pagesToShow: number = 5): (number | string)[] => {
    const pages: (number | string)[] = [];
    const halfPagesToShow = Math.floor(pagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfPagesToShow);
    let endPage = Math.min(totalPages, currentPage + halfPagesToShow);

    if (endPage - startPage + 1 < pagesToShow) {
        if (startPage === 1) {
            endPage = Math.min(totalPages, startPage + pagesToShow - 1);
        } else if (endPage === totalPages) {
            startPage = Math.max(1, totalPages - pagesToShow + 1);
        }
    }

    // Always show the first page
    if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
            pages.push("...");
        }
    }

    // Show pages around the current page
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    // Always show the last page
    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            pages.push("...");
        }
        pages.push(totalPages);
    }

    return Array.from(new Set(pages)); // Remove duplicates in case of small totalPages
};

interface DuplicateTextsTabProps {
    user: User | null;
    deleteTranslationMutation?: UseMutationResult<any, Error, number>;
}

// Helper függvény az outlier javaslatok azonosítására az "Ismétlődések" fülében
const getOutlierSuggestionIds = (occurrences: any[]): Set<number> => {
    const outliers = new Set<number>();

    // Gyűjtsük össze az összes javaslatot tartalom szerint az összes előfordulás összes pending_translations tömbéből
    const suggestionsByContent = new Map<string, number[]>();

    occurrences.forEach(occ => {
        if (occ.pending_translations && Array.isArray(occ.pending_translations)) {
            occ.pending_translations.forEach((trans: any) => {
                const content = trans.content;
                if (!suggestionsByContent.has(content)) {
                    suggestionsByContent.set(content, []);
                }
                suggestionsByContent.get(content)!.push(trans.id);
            });
        }
    });

    // Összeszámlálás - az összes pending_translations az összes előfordulásban
    const totalSuggestions = occurrences.reduce((sum, occ) => {
        return sum + (occ.pending_translations ? occ.pending_translations.length : 0);
    }, 0);

    // Outlierek jelölése - amelyek kevesebb, mint 50% az összes javaslat közül
    const majorityThreshold = totalSuggestions * 0.5;

    suggestionsByContent.forEach((ids, _) => {
        if (ids.length < majorityThreshold && ids.length > 0) {
            ids.forEach(id => outliers.add(id));
        }
    });

    return outliers;
};

const DuplicateTextsTab: React.FC<DuplicateTextsTabProps> = ({ user: propUser, deleteTranslationMutation: propDeleteMutation }) => {
    const { user: hookUser } = useUser();
    const user = propUser || hookUser;
    const { gameId, versionId } = useParams();
    const [page, setPage] = useState(1);
    const queryClient = useQueryClient();

    const { data: gameDetails } = useQuery({
        queryKey: ["gameDetails", gameId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}`);
            return response.json();
        },
        enabled: !!gameId,
    });

    const { data: versionDetails } = useQuery({
        queryKey: ["versionDetails", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}`);
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });
    const [selectedSourceOccId, setSelectedSourceOccId] = useState<number | null>(null);
    const [selectedSourceZipId, setSelectedSourceZipId] = useState<string | null>(null);
    const [newSuggestionInput, setNewSuggestionInput] = useState<{ textId: number | null; value: string }>({ textId: null, value: '' });
    const [hideFullyAccepted, setHideFullyAccepted] = useState(false);
    const [debouncedHideFullyAccepted, setDebouncedHideFullyAccepted] = useState(false);

    // Create local delete mutation if not provided via props
    const deleteTranslationMutation = propDeleteMutation || useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Törlés sikertelen");
            }
            return { success: true };
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás törölve." });
            // Refetch the data
            queryClient.invalidateQueries({ queryKey: ['duplicateTextPage', gameId, page, debouncedHideFullyAccepted] });
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Fetch all translation files for the version
    const { data: allTranslationFiles } = useQuery({
        queryKey: ["allTranslationFiles", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files?all=true`);
            if (!response.ok) throw new Error("Fájlok lekérése sikertelen");
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    // Fetch all uploaded zips for all translation files
    const { data: allUploadedZips } = useQuery({
        queryKey: ["allUploadedZips", gameId, versionId, allTranslationFiles],
        queryFn: async () => {
            if (!allTranslationFiles || allTranslationFiles.length === 0) return [];

            const allZips = [];
            for (const file of allTranslationFiles) {
                const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${file.id}/uploaded_zips`);
                if (response.ok) {
                    const zips = await response.json();
                    allZips.push(...zips);
                }
            }
            return allZips;
        },
        enabled: !!gameId && !!versionId && !!allTranslationFiles,
    });

    // Debounce the hideFullyAccepted filter to avoid excessive queries
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedHideFullyAccepted(hideFullyAccepted);
            setPage(1);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [hideFullyAccepted]);

    const { data, isLoading, isError, error, isFetching } = useQuery({
        queryKey: ['duplicateTextPage', gameId, page, debouncedHideFullyAccepted],
        queryFn: async () => {
            let url = `/api/games/${gameId}/duplicate_texts?page=${page}`;
            if (debouncedHideFullyAccepted) {
                url += '&hide_fully_accepted=true';
            }
            const response = await fetch(url);
            if (!response.ok) throw new Error('A hálózati válasz nem volt megfelelő');
            return response.json();
        },
        enabled: !!gameId,
        placeholderData: keepPreviousData,
    });

    // Reset selectedSourceOccId only when page changes or filter changes, not on every data update
    useEffect(() => {
        setSelectedSourceOccId(null);
    }, [page, debouncedHideFullyAccepted]);

    const applySuggestionMutation = useMutation({
        mutationFn: async ({ textToApply, occurrencesToUpdate, uploadedZipId }: { textToApply: string, occurrencesToUpdate: any[], uploadedZipId: string }) => {
            const { token } = getCsrfToken();
            const userId = getCurrentUserId();
            if (!token || !userId) throw new Error("CSRF token vagy felhasználói azonosító nem található.");

            const occurrenceIds: number[] = [];
            for (const occ of occurrencesToUpdate) {
                occurrenceIds.push(occ.extracted_text_id);
                const saveResponse = await fetch(`/api/extracted_texts/${occ.extracted_text_id}/translate`, {
                    method: "POST",
                    headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
                    body: JSON.stringify({
                        uploaded_zip_id: uploadedZipId,
                        translation: { text_content: textToApply, language: "HU", user_id: userId }
                    }),
                });
                if (!saveResponse.ok) {
                    const errorData = await saveResponse.json();
                    throw new Error(`Fordítás mentése sikertelen a(z) ${occ.extracted_text_id} szöveghez: ${errorData.message || 'Ismeretlen hiba'}`);
                }
                const newTranslation = await saveResponse.json();

                if (newTranslation && newTranslation.id) {
                    const acceptResponse = await fetch(`/api/extracted_texts/${newTranslation.id}/accept_translation`, {
                        method: "PATCH",
                        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
                        body: JSON.stringify({}),
                    });
                    if (!acceptResponse.ok) {
                        const errorData = await acceptResponse.json();
                        throw new Error(`Fordítás elfogadása sikertelen a(z) ${newTranslation.id} fordításhoz: ${errorData.message || 'Ismeretlen hiba'}`);
                    }
                } else {
                    throw new Error("A létrehozott fordításnak nincs azonosítója.");
                }
            }

            return { textApplied: textToApply, occurrenceIds };
        },
        onSuccess: (result) => {
            toast({ title: "Sikeres alkalmazás!", description: "A kiválasztott fordítás alkalmazva lett a megadott helyekre." });

            // Optimistically update the cache
            queryClient.setQueryData(['duplicateTextPage', gameId, page, debouncedHideFullyAccepted], (oldData: any) => {
                if (!oldData || !oldData.occurrences) return oldData;

                return {
                    ...oldData,
                    has_any_accepted_translation: true,
                    occurrences: oldData.occurrences.map((occ: any) => {
                        if (result.occurrenceIds.includes(occ.extracted_text_id)) {
                            return {
                                ...occ,
                                has_accepted_translation: true,
                                accepted_translation: result.textApplied,
                                pending_translations: occ.pending_translations.filter((t: any) => t.content !== result.textApplied)
                            };
                        }
                        return occ;
                    })
                };
            });
        },
        onError: (err: Error) => {
            toast({ title: "Hiba az alkalmazás során", description: err.message, variant: "destructive" });
        }
    });

    const createAndAcceptSuggestionMutation = useMutation({
        mutationFn: async ({ textToApply, occurrenceToUpdate, uploadedZipId }: { textToApply: string, occurrenceToUpdate: any, uploadedZipId: string }) => {
            const { token } = getCsrfToken();
            const userId = getCurrentUserId();
            if (!token || !userId) throw new Error("CSRF token vagy felhasználói azonosító nem található.");

            // 1. Save new translation
            const saveResponse = await fetch(`/api/extracted_texts/${occurrenceToUpdate.extracted_text_id}/translate`, {
                method: "POST",
                headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
                body: JSON.stringify({
                    uploaded_zip_id: uploadedZipId,
                    translation: { text_content: textToApply, language: "HU", user_id: userId }
                }),
            });
            if (!saveResponse.ok) {
                const errorData = await saveResponse.json();
                throw new Error(`Fordítás mentése sikertelen: ${errorData.message || 'Ismeretlen hiba'}`);
            }
            const newTranslation = await saveResponse.json();

            // 2. Accept the newly created translation
            if (newTranslation && newTranslation.id) {
                const acceptResponse = await fetch(`/api/extracted_texts/${newTranslation.id}/accept_translation`, {
                    method: "PATCH",
                    headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
                    body: JSON.stringify({}),
                });
                if (!acceptResponse.ok) {
                    const errorData = await acceptResponse.json();
                    throw new Error(`Fordítás elfogadása sikertelen: ${errorData.message || 'Ismeretlen hiba'}`);
                }
            } else {
                throw new Error("A létrehozott fordításnak nincs azonosítója, így nem lehet elfogadni.");
            }

            return { textApplied: textToApply, occurrenceId: occurrenceToUpdate.extracted_text_id };
        },
        onSuccess: (result) => {
            toast({ title: "Sikeres!", description: "A fordítás létrehozva és elfogadva. Most már kiválaszthatod forrásként." });

            // Optimistically update the cache with the new accepted translation
            queryClient.setQueryData(['duplicateTextPage', gameId, page, debouncedHideFullyAccepted], (oldData: any) => {
                if (!oldData || !oldData.occurrences) return oldData;

                return {
                    ...oldData,
                    has_any_accepted_translation: true,
                    occurrences: oldData.occurrences.map((occ: any) => {
                        if (occ.extracted_text_id === result.occurrenceId) {
                            return {
                                ...occ,
                                has_accepted_translation: true,
                                accepted_translation: result.textApplied,
                                pending_translations: occ.pending_translations.filter((t: any) => t.content !== result.textApplied)
                            };
                        }
                        return occ;
                    })
                };
            });

            setNewSuggestionInput({ textId: null, value: '' });
        },
        onError: (err: Error) => {
            toast({ title: "Hiba a művelet során", description: err.message, variant: "destructive" });
        }
    });

    const acceptMatchingSuggestionsMutation = useMutation({
        mutationFn: async ({ sourceTextId }: { sourceTextId: number }) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");

            const response = await fetch(`/api/games/${gameId}/duplicate_texts/accept_matching_suggestions`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    source_text_id: sourceTextId
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Nem sikerült elfogadni a javaslatok");
            }
            return response.json();
        },
        onSuccess: (result) => {
            // Optimistically update the cache without full page reload
            queryClient.setQueryData(['duplicateTextPage', gameId, page, debouncedHideFullyAccepted], (oldData: any) => {
                if (!oldData || !oldData.occurrences) return oldData;

                // Update accepted counts for all affected occurrences
                return {
                    ...oldData,
                    has_any_accepted_translation: true, // Ensure this flag is true after accepting translations
                    occurrences: oldData.occurrences.map((occ: any) => {
                        // Mark pending translations as accepted if they match the source translation
                        if (occ.pending_translations && occ.pending_translations.length > 0) {
                            const sourceOcc = oldData.occurrences.find((o: any) => o.extracted_text_id === result.source_text_id);
                            if (sourceOcc && sourceOcc.accepted_translation) {
                                const matchingPending = occ.pending_translations.filter(
                                    (t: any) => t.content === sourceOcc.accepted_translation
                                );
                                if (matchingPending.length > 0) {
                                    return {
                                        ...occ,
                                        pending_translations: occ.pending_translations.filter(
                                            (t: any) => t.content !== sourceOcc.accepted_translation
                                        ),
                                        has_accepted_translation: true,
                                        accepted_translation: sourceOcc.accepted_translation,
                                    };
                                }
                            }
                        }
                        return occ;
                    })
                };
            });

            toast({ title: "Sikeres!", description: `${result.accepted_count} db javaslatot fogadtam el a forrás alapján.` });
        },
        onError: (err: Error) => {
            toast({ title: "Hiba az elfogadás során", description: err.message, variant: "destructive" });
        }
    });

    // Local mutation for accepting translations on duplicates page (prevents page reordering)
    const acceptTranslationDuplicatesMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/extracted_texts/${translationId}/accept_translation`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({}),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Fordítás elfogadása sikertelen");
            }
            return response.json();
        },
        onSuccess: (updatedSuggestion) => {
            // Optimistically update the duplicateTextPage cache without full refetch
            queryClient.setQueryData(['duplicateTextPage', gameId, page, debouncedHideFullyAccepted], (oldData: any) => {
                if (!oldData || !oldData.occurrences) return oldData;

                // Update the current occurrence and also update all occurrences with the same content
                const contentOfUpdatedOcc = updatedSuggestion.content;

                return {
                    ...oldData,
                    has_any_accepted_translation: true, // Ensure this flag is true after accepting a translation
                    occurrences: oldData.occurrences.map((occ: any) => {
                        // Find if this occurrence has the translation we just accepted
                        const hasPendingTranslation = occ.pending_translations?.some((t: any) => t.id === updatedSuggestion.id);

                        if (hasPendingTranslation) {
                            // This is the occurrence we just accepted
                            return {
                                ...occ,
                                pending_translations: occ.pending_translations.filter((t: any) => t.id !== updatedSuggestion.id),
                                has_accepted_translation: true,
                                accepted_translation: updatedSuggestion.content,
                            };
                        } else {
                            // Other occurrences with the same content should show the newly accepted translation
                            // This allows the "Automatikus elfogadás" button to find a source
                            if (occ.content === contentOfUpdatedOcc && !occ.has_accepted_translation) {
                                return {
                                    ...occ,
                                    has_accepted_translation: true,
                                    accepted_translation: updatedSuggestion.content,
                                };
                            }
                            return occ;
                        }
                    })
                };
            });

            // Don't refetch - optimistic update is sufficient, prevents page jump and selection reset
            toast({ title: "Fordítás sikeresen elfogadva!" });
        },
        onError: (error) => {
            alert(`Hiba a fordítás elfogadása során: ${error.message}`);
        }
    });

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && (!data || newPage <= data.total_pages)) {
            setPage(newPage);
        }
    };

    const [inputPage, setInputPage] = useState<number | string>(page.toString());

    useEffect(() => {
        setInputPage(page.toString());
    }, [page]);

    const handleInputPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputPage(e.target.value);
    };

    const handleInputPageKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            const newPage = Number(inputPage);
            const totalPages = data?.total_pages || 1;

            if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
                handlePageChange(newPage);
            } else {
                toast({
                    title: "Érvénytelen oldal szám",
                    description: `Kérjük, adjon meg egy számot 1 és ${totalPages} között.`,
                    variant: "destructive",
                });
                setInputPage(page.toString());
            }
        }
    };

    const handleApply = (applyTo: 'all' | 'selected') => {
        if (!selectedSourceOccId) {
            toast({ title: "Hiba", description: "Válassz ki egy forrást az alkalmazáshoz!", variant: "destructive" });
            return;
        }
        if (!selectedSourceZipId) {
            toast({ title: "Hiba", description: "Nincs forrásfájl kiválasztva a fordításhoz.", variant: "destructive" });
            return;
        }

        const sourceOccurrence = data.occurrences.find((occ: any) => occ.extracted_text_id === selectedSourceOccId);
        if (!sourceOccurrence || !sourceOccurrence.has_accepted_translation) {
            toast({ title: "Hiba", description: "A kiválasztott forrás érvénytelen.", variant: "destructive" });
            return;
        }
        const textToApply = sourceOccurrence.accepted_translation;

        let occurrencesToUpdate: any[] = [];
        if (applyTo === 'selected') {
            // This mode is no longer used since we removed the target selection
            occurrencesToUpdate = [];
        } else { // 'all'
            occurrencesToUpdate = data.occurrences.filter((occ: any) => !occ.has_accepted_translation && occ.pending_translations.length === 0);
        }

        if (occurrencesToUpdate.length === 0) {
            toast({ title: "Nincs mit tenni", description: "Nincsenek érvényes célhelyek a művelethez.", variant: "default" });
            return;
        }

        applySuggestionMutation.mutate({
            textToApply: textToApply,
            occurrencesToUpdate: occurrencesToUpdate,
            uploadedZipId: selectedSourceZipId,
        });
    };

    const handleCreateAndAcceptSuggestion = () => {
        if (!newSuggestionInput.textId || newSuggestionInput.value.trim() === '') {
            toast({ title: "Hiba", description: "A fordítás nem lehet üres.", variant: "destructive" });
            return;
        }

        const occurrenceToUpdate = data.occurrences.find((occ: any) => occ.extracted_text_id === newSuggestionInput.textId);
        if (!occurrenceToUpdate) {
            toast({ title: "Hiba", description: "A cél előfordulás nem található.", variant: "destructive" });
            return;
        }

        if (!occurrenceToUpdate.uploaded_zip_id) {
            toast({ title: "Hiba", description: "Az előfordulás ZIP azonosítója nem található.", variant: "destructive" });
            return;
        }

        createAndAcceptSuggestionMutation.mutate({
            textToApply: newSuggestionInput.value,
            occurrenceToUpdate: occurrenceToUpdate,
            uploadedZipId: occurrenceToUpdate.uploaded_zip_id.toString(),
        });
    };



    // Outlier javaslatok detektálása
    const outlierIds = useMemo(() => {
        if (!data || !data.occurrences) return new Set<number>();
        return getOutlierSuggestionIds(data.occurrences);
    }, [data]);

    const [expandedGeminiRows, setExpandedGeminiRows] = useState<Set<string | number>>(new Set());
    const [duplicateGeminiResults, setDuplicateGeminiResults] = useState<Map<string | number, string>>(new Map());

    const toggleGeminiRow = (suggestionId: string | number) => {
        setExpandedGeminiRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(suggestionId)) {
                newSet.delete(suggestionId);
            } else {
                newSet.add(suggestionId);
            }
            return newSet;
        });
    };

    const duplicateGeminiCheckMutation = useMutation({
        mutationFn: async ({ suggestionId, sourceText, targetText, mode }: { suggestionId: string | number; sourceText: string; targetText: string; mode?: string }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const requestBody: any = {
                source_text: sourceText,
                target_text: targetText
            };

            if (mode === "suggest") {
                requestBody.mode = "suggest";
            }

            const response = await fetch(`/api/gemini/check_translation`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Gemini API hívás sikertelen");
            }
            const result = await response.json();
            return { suggestionId, result: result.result };
        },
        onSuccess: ({ suggestionId, result }) => {
            setDuplicateGeminiResults(prev => new Map(prev).set(suggestionId, result));
            toast({ title: "Gemini ellenőrzés sikeres!" });
        },
        onError: (error, { suggestionId }) => {
            setDuplicateGeminiResults(prev => new Map(prev).set(suggestionId, `Hiba: ${error.message}`));
            toast({ title: "Gemini ellenőrzés sikertelen!", description: error.message, variant: "destructive" });
        },
    });

    if (isLoading) return <p>Ismétlődő szöveg betöltése...</p>;
    if (isError) return <p>Hiba: {error.message}</p>;

    return (
        <div className="container mx-auto p-4 space-y-8 bg-background text-foreground">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}`}>{gameDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}/versions/${versionId}`}>Verzió: {versionDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>Ismétlődések</BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <VersionNavigation gameId={gameId} versionId={versionId} />
            <Card>
                <CardHeader>
                    <CardTitle>Ismétlődő szövegek</CardTitle>
                    <CardDescription>
                        Lapozz az ismétlődő szövegek között. Összesen {data?.total_pages || 0} egyedi ismétlődő szöveg van.
                    </CardDescription>
                    <div className="flex items-center space-x-2 pt-2">
                        <Switch
                            id="hide-fully-accepted"
                            checked={hideFullyAccepted}
                            onCheckedChange={setHideFullyAccepted}
                        />
                        <Label htmlFor="hide-fully-accepted">Teljesen lefordított csoportok elrejtése</Label>
                    </div>
                </CardHeader>
                <CardContent>
                    {data && data.occurrences && data.occurrences.length > 0 ? (
                        <div className={`transition-opacity duration-300 ${isFetching || applySuggestionMutation.isPending || createAndAcceptSuggestionMutation.isPending || acceptMatchingSuggestionsMutation.isPending ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                            {data && data.total_pages > 1 && (
                                <div className="flex justify-center pb-4 mb-4 border-b">
                                    <div className="flex space-x-1 items-center">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(1)}
                                            disabled={!data || page === 1}
                                        >
                                            {"<<"}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={!data || page <= 1}
                                        >
                                            {"<"}
                                        </Button>
                                        {data?.total_pages && generatePaginationPages(page, data.total_pages).map((p, index) => (
                                            p === "..." ? (
                                                <span key={index} className="px-2 py-1 text-sm">...</span>
                                            ) : (
                                                <Button
                                                    key={index}
                                                    variant={p === page ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => handlePageChange(Number(p))}
                                                >
                                                    {p}
                                                </Button>
                                            )
                                        ))}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={!data || page >= data.total_pages}
                                        >
                                            {">"}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(data.total_pages || 1)}
                                            disabled={!data || page === data.total_pages}
                                        >
                                            {">>"}
                                        </Button>
                                        <span className="ml-4 text-sm text-muted-foreground">
                                            {page}/{data.total_pages}
                                        </span>
                                        <div className="ml-4 flex items-center space-x-2">
                                            <label htmlFor="jump-page-duplicate" className="text-sm text-muted-foreground">Ugrás:</label>
                                            <input
                                                id="jump-page-duplicate"
                                                type="number"
                                                value={inputPage}
                                                onChange={handleInputPageChange}
                                                onKeyPress={handleInputPageKeyPress}
                                                className="w-16 text-center bg-transparent border border-input rounded-md dark:text-white text-sm"
                                                min="1"
                                                max={data?.total_pages || 1}
                                                placeholder="1"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="mb-4 p-4 border rounded-lg bg-muted">
                                <h3 className="text-lg font-semibold">"{data.content}"</h3>
                                <p className="text-sm text-muted-foreground">
                                    Ez a szöveg <span className="font-bold">{data.count}</span> alkalommal fordul elő.
                                    {data.has_any_accepted_translation ?
                                        <span className="text-green-600 font-semibold ml-2">(Van elfogadott fordítása)</span> :
                                        <span className="text-amber-600 font-semibold ml-2">(Nincs még elfogadott fordítása)</span>
                                    }
                                </p>
                            </div>
                            {user?.admin && (
                                <div className="flex flex-col md:flex-row gap-4 mb-4">
                                    <Card className="flex-1">
                                        <CardHeader>
                                            <CardTitle>Elfogadott fordítás alkalmazása</CardTitle>
                                            <CardDescription>
                                                {data.has_any_accepted_translation
                                                    ? "Válassz egy FORRÁST, majd CÉL(oka)t a táblázatból, vagy alkalmazd az összes hiányzó helyre."
                                                    : "Először adj meg egy fordítást valamelyik elemhez a 'Javaslatok' oszlopban. Az elfogadás után az forrásként használható lesz."}
                                            </CardDescription>
                                        </CardHeader>
                                        {data.has_any_accepted_translation && (
                                            <CardContent className="space-y-4">
                                                <div>
                                                    <Label htmlFor="source-zip-select">Forrásfájl kiválasztása</Label>
                                                    <select
                                                        id="source-zip-select"
                                                        value={selectedSourceZipId || ""}
                                                        onChange={(e) => setSelectedSourceZipId(e.target.value || null)}
                                                        className="w-full border rounded-md px-3 py-2 bg-background text-foreground mt-2"
                                                    >
                                                        <option value="">-- Válassz forrásfájlt --</option>
                                                        {allUploadedZips && allUploadedZips.length > 0 ? (
                                                            allUploadedZips.map((zip: any) => (
                                                                <option key={zip.id} value={zip.id}>
                                                                    {zip.label} ({zip.language}) - {zip.status}
                                                                </option>
                                                            ))
                                                        ) : (
                                                            <option value="" disabled>Nincsenek feltöltött fájlok</option>
                                                        )}
                                                    </select>
                                                </div>
                                                <Button onClick={() => handleApply('all')} disabled={!selectedSourceOccId || !selectedSourceZipId || applySuggestionMutation.isPending} variant="secondary">
                                                    {applySuggestionMutation.isPending ? 'Alkalmazás...' : `Alkalmazás a javaslattal nem rendelkezőkre`}
                                                </Button>
                                            </CardContent>
                                        )}
                                    </Card>
                                    {data.has_any_accepted_translation && (
                                        <Card className="flex-1 border-green-700 dark:border-green-600 bg-green-50 dark:bg-green-950">
                                            <CardHeader>
                                                <CardTitle className="text-green-700 dark:text-green-400">Automatikus elfogadás</CardTitle>
                                                <CardDescription className="dark:text-green-200/70">
                                                    Az összes olyan javaslatot fogad el ezen az oldalon, amely pont ugyanez a fordítás, mint a kiválasztott forrásnak.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                {selectedSourceOccId ? (
                                                    <div className="space-y-3">
                                                        <p className="text-sm">
                                                            <strong>Kiválasztott forrás:</strong> "<span className="text-green-700 dark:text-green-400 font-semibold">{data.occurrences.find((occ: any) => occ.extracted_text_id === selectedSourceOccId)?.accepted_translation}</span>"
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            Az összes többi előfordulásban lévő olyan javaslatot fogad el, amely pont ennek a szövegnek felel meg.
                                                        </p>
                                                        <Button
                                                            onClick={() => {
                                                                if (selectedSourceOccId) {
                                                                    acceptMatchingSuggestionsMutation.mutate({
                                                                        sourceTextId: selectedSourceOccId
                                                                    });
                                                                }
                                                            }}
                                                            disabled={acceptMatchingSuggestionsMutation.isPending || !selectedSourceOccId}
                                                            className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                                                        >
                                                            {acceptMatchingSuggestionsMutation.isPending ? 'Elfogadás...' : 'Az ilyen javaslatok elfogadása'}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">Válassz egy FORRÁST a táblázatból az automatikus elfogadás aktiválásához.</p>
                                                )}
                                            </CardContent>
                                        </Card>
                                    )}
                                </div>
                            )}
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        {user?.admin && <TableHead className="text-center w-8 p-2">✓</TableHead>}
                                        <TableHead className="text-center p-1 font-semibold text-xs">ver:</TableHead>
                                        <TableHead className="text-center p-1 font-semibold text-xs">Fájl</TableHead>
                                        <TableHead className="text-center p-1 font-semibold text-xs">Hely</TableHead>
                                        <TableHead className="flex-1">Eredeti szöveg</TableHead>
                                        <TableHead className="flex-1">Elfogadott fordítás</TableHead>
                                        <TableHead className="flex-1">Javaslatok</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.occurrences.map((occ: any) => (
                                        <>
                                            <TableRow data-state={selectedSourceOccId === occ.extracted_text_id ? 'selected' : 'unselected'} className="bg-white dark:bg-slate-950 text-gray-900 dark:text-white">
                                                {user?.admin && (
                                                    <TableCell className="text-center p-2 w-8" rowSpan={2}>
                                                        <Checkbox
                                                            id={`source-select-${occ.extracted_text_id}`}
                                                            disabled={!occ.has_accepted_translation}
                                                            checked={selectedSourceOccId === occ.extracted_text_id}
                                                            onCheckedChange={(checked) => setSelectedSourceOccId(checked ? occ.extracted_text_id : null)}
                                                        />
                                                    </TableCell>
                                                )}
                                                <TableCell colSpan={3} className="p-1">
                                                    <a href={`/games/${occ.game_id}/versions/${occ.version_id}/translate?highlight_id=${occ.extracted_text_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-xs truncate block max-w-xs" title={`V: ${occ.version_name} | F: ${occ.file_name} | H: ${occ.name}`}>
                                                        {occ.name.substring(0, 30)}...
                                                    </a>
                                                </TableCell>
                                                <TableCell className="p-2" rowSpan={2}>
                                                    <div className="text-sm break-words">{data.content}</div>
                                                </TableCell>
                                                <TableCell className="p-2" rowSpan={2}>
                                                    {occ.has_accepted_translation ? (
                                                        <div className="text-sm break-words">"{occ.accepted_translation}"</div>
                                                    ) : (
                                                        <span className="text-muted-foreground text-xs">Nincs</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="p-2" rowSpan={2}>
                                                    {occ.pending_translations.length > 0 ? (
                                                        <>
                                                            <ul className="space-y-2 max-h-[200px] overflow-y-auto">
                                                                {occ.pending_translations.map((p: any) => {
                                                                    const isOutlier = outlierIds.has(p.id);
                                                                    return (
                                                                        <li key={p.id} className={`flex justify-between items-start p-1 rounded text-xs ${p.status === 'rejected' ? 'opacity-60' : ''
                                                                            } ${isOutlier ? 'border-l-2 border-yellow-500 pl-2' : ''
                                                                            }`}>
                                                                            <div className="flex-1">
                                                                                <div className="flex items-center gap-1">
                                                                                    {isOutlier && (
                                                                                        <span className="text-yellow-500 font-bold text-lg" title="Ez a fordítás eltér a többitől!">
                                                                                            ⚠️
                                                                                        </span>
                                                                                    )}
                                                                                    <p className="break-words">"{p.content}"</p>
                                                                                </div>
                                                                                <p className="text-xs text-muted-foreground">{p.user || 'ismeretlen'} {p.status === 'rejected' ? '(Elutasítva)' : ''}</p>
                                                                            </div>
                                                                            {user?.admin && (
                                                                                <div className="flex items-center space-x-1 ml-1 flex-shrink-0">
                                                                                    <Button
                                                                                        variant="default"
                                                                                        size="sm"
                                                                                        onClick={() => acceptTranslationDuplicatesMutation.mutate(p.id)}
                                                                                        disabled={acceptTranslationDuplicatesMutation.isPending || p.status === 'rejected'}
                                                                                        className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                                                                                    >
                                                                                        {acceptTranslationDuplicatesMutation.isPending && acceptTranslationDuplicatesMutation.variables === p.id ? '...' : <CheckIcon className="w-4 h-4" />}
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="destructive"
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            if (window.confirm("Biztosan törölni szeretnéd ezt a javaslatot?")) {
                                                                                                deleteTranslationMutation.mutate(p.id);
                                                                                            }
                                                                                        }}
                                                                                        disabled={deleteTranslationMutation.isPending || p.status === 'rejected'}
                                                                                    >
                                                                                        {deleteTranslationMutation.isPending && deleteTranslationMutation.variables === p.id ? '...' : 'X'}
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="outline"
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            toggleGeminiRow(p.id);
                                                                                            if (!duplicateGeminiResults.has(p.id) && !expandedGeminiRows.has(p.id)) {
                                                                                                duplicateGeminiCheckMutation.mutate({
                                                                                                    suggestionId: p.id,
                                                                                                    sourceText: data.content,
                                                                                                    targetText: p.content
                                                                                                });
                                                                                            }
                                                                                        }}
                                                                                        className="w-8 h-8 p-0"
                                                                                        title="Gemini ellenőrzés"
                                                                                    >
                                                                                        {duplicateGeminiCheckMutation.isPending && duplicateGeminiCheckMutation.variables?.suggestionId === p.id ? '...' : '?'}
                                                                                    </Button>
                                                                                </div>
                                                                            )}
                                                                            {(user?.admin || user?.translater_admin) && (
                                                                                <div className="flex items-center space-x-1 ml-1 flex-shrink-0">
                                                                                    <Button
                                                                                        variant="default"
                                                                                        size="sm"
                                                                                        onClick={() => acceptTranslationDuplicatesMutation.mutate(p.id)}
                                                                                        disabled={acceptTranslationDuplicatesMutation.isPending || p.status === 'rejected'}
                                                                                        className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                                                                                    >
                                                                                        {acceptTranslationDuplicatesMutation.isPending && acceptTranslationDuplicatesMutation.variables === p.id ? '...' : <CheckIcon className="w-4 h-4" />}
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="destructive"
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            if (window.confirm("Biztosan törölni szeretnéd ezt a javaslatot?")) {
                                                                                                deleteTranslationMutation.mutate(p.id);
                                                                                            }
                                                                                        }}
                                                                                        disabled={deleteTranslationMutation.isPending || p.status === 'rejected'}
                                                                                    >
                                                                                        {deleteTranslationMutation.isPending && deleteTranslationMutation.variables === p.id ? '...' : 'X'}
                                                                                    </Button>
                                                                                </div>
                                                                            )}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </>
                                                    ) : !occ.has_accepted_translation && (user?.admin || user?.translater_admin) ? (
                                                        <div>
                                                            {newSuggestionInput.textId === occ.extracted_text_id ? (
                                                                <div className="flex flex-col space-y-2 items-start w-full">
                                                                    <div className="flex gap-2 items-center w-full">
                                                                        <Input
                                                                            placeholder="Új fordítás..."
                                                                            value={newSuggestionInput.value}
                                                                            onChange={(e) => setNewSuggestionInput({ textId: occ.extracted_text_id, value: e.target.value })}
                                                                            className="max-w-[200px] text-xs"
                                                                        />
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                toggleGeminiRow(`new-${occ.extracted_text_id}`);
                                                                                if (!duplicateGeminiResults.has(`new-${occ.extracted_text_id}`)) {
                                                                                    duplicateGeminiCheckMutation.mutate({
                                                                                        suggestionId: `new-${occ.extracted_text_id}` as any,
                                                                                        sourceText: data.content,
                                                                                        targetText: newSuggestionInput.value.trim() || "(nincs beírva)",
                                                                                        mode: "suggest" // Új mód: kontextus-alapú javaslatok
                                                                                    } as any);
                                                                                }
                                                                            }}
                                                                            className="w-8 h-8 p-0 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 border-purple-300"
                                                                            title="Gemini kontextus-alapú javaslatok"
                                                                        >
                                                                            {duplicateGeminiCheckMutation.isPending && duplicateGeminiCheckMutation.variables?.suggestionId === `new-${occ.extracted_text_id}` ? '...' : '💡'}
                                                                        </Button>
                                                                    </div>
                                                                    <div className="flex space-x-2">
                                                                        <Button size="sm" onClick={handleCreateAndAcceptSuggestion} disabled={createAndAcceptSuggestionMutation.isPending}>
                                                                            {createAndAcceptSuggestionMutation.isPending ? 'Mentés...' : 'Ment és Elfogad'}
                                                                        </Button>
                                                                        <Button size="sm" variant="ghost" onClick={() => setNewSuggestionInput({ textId: null, value: '' })}>Mégse</Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex gap-2 items-center">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => setNewSuggestionInput({ textId: occ.extracted_text_id, value: '' })}
                                                                        disabled={newSuggestionInput.textId !== null}
                                                                    >
                                                                        Fordítást adok meg
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-muted-foreground">Nincs</p>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow className="bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-white">
                                                <TableCell className="text-xs p-0.5 text-center whitespace-nowrap">
                                                    <div className="font-mono text-xs">{occ.version_name}</div>
                                                </TableCell>
                                                <TableCell className="text-xs p-0.5 text-center">
                                                    <div className="font-mono text-xs truncate max-w-[60px]" title={occ.file_name}>{occ.file_name}</div>
                                                </TableCell>
                                                <TableCell className="text-xs p-0.5 text-center">
                                                    <div className="text-xs truncate max-w-[60px]" title={occ.name}>{occ.name}</div>
                                                </TableCell>
                                            </TableRow>
                                            {(occ.pending_translations.some((p: any) => expandedGeminiRows.has(p.id)) || expandedGeminiRows.has(`new-${occ.extracted_text_id}`)) && (
                                                <TableRow className="bg-blue-50 dark:bg-blue-950">
                                                    <TableCell colSpan={7} className="p-3">
                                                        <div className="space-y-3">
                                                            {/* 💡 gomb eredménye - új fordítás javaslat */}
                                                            {expandedGeminiRows.has(`new-${occ.extracted_text_id}`) && (
                                                                <div className="p-3 bg-purple-50 dark:bg-purple-900 border border-purple-300 dark:border-purple-700 rounded">
                                                                    <div className="flex items-start gap-2 mb-2">
                                                                        <span className="font-semibold text-purple-900 dark:text-purple-100 flex-shrink-0">💡 Gemini fordítási javaslatok:</span>
                                                                    </div>
                                                                    <p className="text-sm text-purple-800 dark:text-purple-200 break-words whitespace-pre-wrap">
                                                                        {duplicateGeminiResults.has(`new-${occ.extracted_text_id}`)
                                                                            ? duplicateGeminiResults.get(`new-${occ.extracted_text_id}`)
                                                                            : "Betöltés..."}
                                                                    </p>
                                                                </div>
                                                            )}
                                                            {/* ? gomb eredménye - meglévő fordítás ellenőrzése */}
                                                            {occ.pending_translations.map((p: any) => {
                                                                if (expandedGeminiRows.has(p.id)) {
                                                                    const geminiText = duplicateGeminiResults.has(p.id)
                                                                        ? duplicateGeminiResults.get(p.id)
                                                                        : "Betöltés...";
                                                                    return (
                                                                        <div key={`gemini-full-${p.id}`} className="p-3 bg-white dark:bg-gray-900 border border-blue-200 dark:border-blue-800 rounded">
                                                                            <div className="flex items-start gap-2 mb-2">
                                                                                <span className="font-semibold text-blue-900 dark:text-blue-100 flex-shrink-0">Fordítás: "{p.content}"</span>
                                                                            </div>
                                                                            <p className="text-sm text-blue-800 dark:text-blue-200 break-words whitespace-pre-wrap">
                                                                                <span className="font-semibold">Gemini ellenőrzés:</span> {geminiText}
                                                                            </p>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <p>Nincsenek a szűrőnek megfelelő ismétlődő szövegek ebben a játékban.</p>
                    )}
                </CardContent>
                {data && data.total_pages > 1 && (
                    <CardFooter className="flex justify-center pt-4">
                        <div className="flex space-x-1 items-center">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(1)}
                                disabled={!data || page === 1}
                            >
                                {"<<"}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={!data || page <= 1}
                            >
                                {"<"}
                            </Button>
                            {data?.total_pages && generatePaginationPages(page, data.total_pages).map((p, index) => (
                                p === "..." ? (
                                    <span key={index} className="px-2 py-1 text-sm">...</span>
                                ) : (
                                    <Button
                                        key={index}
                                        variant={p === page ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handlePageChange(Number(p))}
                                    >
                                        {p}
                                    </Button>
                                )
                            ))}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={!data || page >= data.total_pages}
                            >
                                {">"}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(data.total_pages || 1)}
                                disabled={!data || page === data.total_pages}
                            >
                                {">>"}
                            </Button>
                            <input
                                type="number"
                                value={inputPage}
                                onChange={handleInputPageChange}
                                onKeyPress={handleInputPageKeyPress}
                                className="w-20 text-center bg-transparent border border-input rounded-md dark:text-white"
                                min="1"
                                max={data?.total_pages || 1}
                            />
                            <span className="text-sm text-muted-foreground"> / {data?.total_pages || 1}</span>
                        </div>
                    </CardFooter>
                )}
            </Card>
        </div>
    );
};

export default DuplicateTextsTab;

// Wrapper component for routing
export function Gomb5Page() {
    const { user } = useUser();
    return <DuplicateTextsTab user={user} />;
}